import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faInfoCircle, 
  faCheck, 
  faExclamationTriangle,
  faEquals
} from '@fortawesome/free-solid-svg-icons';
import {
  renderCombinedRows,
  renderStandardRows,
  renderV2Rows,
  getFooterData
} from './transaction/tableUtils';
import { getBalanceLabel } from '../utils/formatters';

const TransactionTable = ({
  onRefresh,
  data, 
  type, 
  soldeCourant,
  soldeCourantClass,
  soldeCumulatif,
  soldeCumulatifClass,
  soldeFinal,
  soldeFinalClass,
  soldeFinalV2,
  v2SoldeClass,
  regularSoldeClass
}) => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  const handleRefresh = () => {
    if (onRefresh && typeof onRefresh === 'function') {
      console.log('TransactionTable calling onRefresh ONCE');
      onRefresh();
    } else {
      console.warn('onRefresh is not provided or not a function - cannot refresh data from server');
    }
  };

  if (!data || !data.periodesSorted || !data.periodes) {
    return <div>Aucune donnée de transaction disponible</div>;
  }
  
  // Render different row content based on table type
  const renderRows = () => {
    switch (type) {
      case 'combined':
        return renderCombinedRows(data, data.soldeCumulatif, handleRefresh);
      case 'standard':
        return renderStandardRows(data, data.soldeCumulatif, handleRefresh);
      case 'v2':
        return renderV2Rows(data, data.soldeCumulatif, handleRefresh);
      default:
        return <tr><td colSpan="5">Type de table inconnu</td></tr>;
    }
  };
  
  const footerData = getFooterData(
    type, 
    data, 
    soldeCourant, 
    soldeCourantClass, 
    soldeCumulatif, 
    soldeCumulatifClass, 
    soldeFinal, 
    soldeFinalClass, 
    soldeFinalV2, 
    v2SoldeClass
  );

  // Type-specific headers
  const renderTypeHeader = () => {
    if (type === 'standard') {
      return (
        <div className="accounting-type-header standard">
          <strong>Comptabilité Standard</strong> - Virements bancaires, transactions électroniques
        </div>
      );
    } else if (type === 'v2') {
      return (
        <div className="accounting-type-header v2">
          <strong>Comptabilité V2</strong> - Paiements en espèces, PayPal, méthodes manuelles
        </div>
      );
    }
    return null;
  };
  
  // Get balance icon based on amount
  const getBalanceIcon = (amount) => {
    if (typeof amount !== 'number') return null;
    if (amount < 0) return <FontAwesomeIcon icon={faExclamationTriangle} style={{color: '#b31412', marginRight: '4px'}} />;
    if (amount > 0) return <FontAwesomeIcon icon={faCheck} style={{color: '#0f733c', marginRight: '4px'}} />;
    return <FontAwesomeIcon icon={faEquals} style={{color: '#5f6368', marginRight: '4px'}} />;
  };

  return (
    <>
      {renderTypeHeader()}
      
      <table className="compact-history">
        <thead>
          <tr>
            <th>Période</th>
            <th>
              Facture
              {type === 'standard' && <span className="accounting-badge standard" style={{marginLeft: '5px'}}>Standard</span>}
              {type === 'v2' && <span className="accounting-badge v2" style={{marginLeft: '5px'}}>V2</span>}
            </th>
            <th>
              Paiements
              {type === 'standard' && <span className="accounting-badge standard" style={{marginLeft: '5px'}}>Standard</span>}
              {type === 'v2' && <span className="accounting-badge v2" style={{marginLeft: '5px'}}>V2</span>}
            </th>
            <th>Balance mensuelle</th>
            <th>Total à date</th>
          </tr>
        </thead>
        <tbody>
          {renderRows()}
        </tbody>
        <tfoot>
          <tr className="total-row">
            <td><strong>{footerData.title || 'TOTAL'}</strong></td>
            <td><strong>{footerData.factureTotal}€</strong></td>
            <td><strong>{footerData.paymentTotal}€</strong></td>
            <td className={footerData.soldeClass}>
              <div className="balance-info">
                {getBalanceIcon(footerData.solde)}
                <strong>{footerData.solde}€</strong>
                <FontAwesomeIcon icon={faInfoCircle} className="balance-info-icon" />
                <span className="balance-tooltip">
                  {getBalanceLabel(footerData.solde)}
                </span>
              </div>
              <span className="balance-label">
                Solde {type === 'standard' ? 'standard' : type === 'v2' ? 'V2' : 'actuel'}
              </span>
            </td>
            <td className={footerData.cumulativeSoldeClass}>
              <div className="balance-info">
                {getBalanceIcon(footerData.cumulativeSolde)}
                <strong>{footerData.cumulativeSolde}€</strong>
                <FontAwesomeIcon icon={faInfoCircle} className="balance-info-icon" />
                <span className="balance-tooltip">
                  {getBalanceLabel(footerData.cumulativeSolde)}
                </span>
              </div>
              <span className="balance-label">
                Total {type === 'standard' ? 'standard ' : type === 'v2' ? 'V2 ' : ''} cumulé
              </span>
            </td>
          </tr>
        </tfoot>
      </table>

      {/* Légende explicative */}
      <div className="transaction-legend">
        <div className="legend-item">
          <span className="legend-color credit"></span>
          <span>Crédit (en faveur du locataire)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color debit"></span>
          <span>Débit (dû par le locataire)</span>
        </div>
        {type === 'combined' && (
          <>
            <div className="legend-item">
              <span className="legend-color standard"></span>
              <span>Standard</span>
            </div>
            <div className="legend-item">
              <span className="legend-color v2"></span>
              <span>V2</span>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default TransactionTable;
